import { useState } from 'react';
import SubTaskList from './SubTaskList';
import { makeTask, uid } from './utils';
import { themes } from './theme';

export default function TemplateManager({ templates, onSave, onDelete, onClose, theme = themes.light }) {
  const [name, setName] = useState('');
  const [tasks, setTasks] = useState([makeTask()]);
  const [editingId, setEditingId] = useState(null);

  function startEdit(tpl) {
    setEditingId(tpl.id);
    setName(tpl.name);
    setTasks(JSON.parse(JSON.stringify(tpl.tasks)));
  }

  function reset() {
    setEditingId(null);
    setName('');
    setTasks([makeTask()]);
  }

  function save() {
    if (!name.trim()) return;
    onSave({ id: editingId || uid(), name: name.trim(), tasks: JSON.parse(JSON.stringify(tasks)) });
    reset();
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: theme.overlay,
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        background: theme.glassStrong || theme.surface,
        backdropFilter: theme.glassBlur,
        WebkitBackdropFilter: theme.glassBlur,
        border: `1px solid ${theme.border}`,
        borderRadius: 10,
        width: '100%',
        maxWidth: 680,
        maxHeight: '88vh',
        overflowY: 'auto',
        padding: 24,
        boxShadow: theme.shadowStrong,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ color: theme.primary, margin: 0, fontSize: 22, fontWeight: 900, fontFamily: "'Orbitron', sans-serif" }}>
            Template Library
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: theme.faint, cursor: 'pointer', fontSize: 22 }}>x</button>
        </div>

        {templates.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <div style={sectionLabel(theme)}>Saved Templates</div>
            {templates.map(tpl => (
              <div key={tpl.id} style={{
                background: theme.surfaceSoft,
                border: `1px solid ${theme.borderSoft}`,
                borderRadius: 8,
                padding: '10px 14px',
                marginBottom: 8,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 12,
              }}>
                <div>
                  <div style={{ color: theme.text, fontWeight: 700 }}>{tpl.name}</div>
                  <div style={{ color: theme.muted, fontSize: 11, marginTop: 2 }}>{tpl.tasks.length} top-level tasks</div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => startEdit(tpl)} style={smallPrimary(theme)}>Edit</button>
                  <button onClick={() => { if (window.confirm('Delete this template?')) onDelete(tpl.id); }} style={smallDanger(theme)}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div style={{ background: theme.surfaceSoft, borderRadius: 8, padding: 16, border: `1px solid ${theme.borderSoft}` }}>
          <div style={sectionLabel(theme)}>
            {editingId ? 'Edit Template' : 'New Template'}
          </div>
          <input
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Template name"
            style={{
              width: '100%',
              background: theme.surface,
              border: `1px solid ${theme.border}`,
              borderRadius: 6,
              color: theme.text,
              padding: '8px 12px',
              fontSize: 13,
              fontFamily: 'inherit',
              boxSizing: 'border-box',
              outline: 'none',
              marginBottom: 12,
            }}
          />
          <div style={{ color: theme.muted, fontSize: 11, marginBottom: 8 }}>Tasks and subtasks:</div>
          <SubTaskList tasks={tasks} onChange={setTasks} depth={1} theme={theme} />
          <button onClick={() => setTasks(t => [...t, makeTask()])} style={{
            marginTop: 8,
            background: theme.secondarySoft,
            border: `1px dashed ${theme.secondary}`,
            borderRadius: 5,
            color: theme.secondary,
            fontSize: 12,
            cursor: 'pointer',
            padding: '4px 12px',
            fontFamily: 'inherit',
            fontWeight: 800,
          }}>▸ Add Task</button>

          <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
            <button onClick={save} style={{
              background: `linear-gradient(135deg, ${theme.primary}, ${theme.secondary})`,
              border: `1px solid ${theme.primary}`,
              borderRadius: 7,
              color: '#ffffff',
              cursor: 'pointer',
              padding: '8px 18px',
              fontSize: 13,
              fontFamily: 'inherit',
              fontWeight: 700,
            }}>{editingId ? '◆ Update' : '◆ Save'} Template</button>
            {editingId && (
              <button onClick={reset} style={{
                background: theme.surface,
                border: `1px solid ${theme.border}`,
                borderRadius: 7,
                color: theme.text,
                cursor: 'pointer',
                padding: '8px 14px',
                fontSize: 13,
                fontFamily: 'inherit',
              }}>Cancel</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function sectionLabel(theme) {
  return {
  color: theme.muted,
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: 1,
  marginBottom: 10,
  fontWeight: 800,
};
}

function smallPrimary(theme) {
  return {
  background: theme.primarySoft,
  border: `1px solid ${theme.primary}`,
  borderRadius: 6,
  color: theme.primary,
  cursor: 'pointer',
  padding: '4px 12px',
  fontSize: 12,
  fontFamily: 'inherit',
  fontWeight: 700,
};
}

function smallDanger(theme) {
  return {
  background: theme.dangerSoft,
  border: `1px solid ${theme.danger}`,
  borderRadius: 6,
  color: theme.danger,
  cursor: 'pointer',
  padding: '4px 12px',
  fontSize: 12,
  fontFamily: 'inherit',
  fontWeight: 700,
};
}
